import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Calendar, Tag } from 'lucide-react';

export default function EventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        if (!id) {
          setError('이벤트 ID가 없습니다.');
          setIsLoading(false);
          return;
        }

        const eventDocRef = doc(db, 'events', id);
        const eventDocSnap = await getDoc(eventDocRef);

        if (eventDocSnap.exists()) {
          setEvent({ id: eventDocSnap.id, ...eventDocSnap.data() });
        } else {
          setError('해당 이벤트를 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError('이벤트 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">오류</h2>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="mx-auto pb-12">
        {event.image_url && (
          <img src={event.image_url} alt={event.title} className="w-full h-96 object-cover" />
        )}
        <div className="bg-white rounded-lg shadow-lg pb-6 px-6 md:p-8 min-h-screen">
            <div className="flex items-center space-x-4 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${event.event_type === 'discount' ? 'bg-red-100 text-red-800' :
                  event.event_type === 'special' ? 'bg-purple-100 text-purple-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                  <Tag className="w-4 h-4 mr-1.5" />
                  {event.event_type === 'discount' ? `할인 (${event.discount_rate}%)` :
                    event.event_type === 'special' ? '특별 이벤트' : '일반 이벤트'}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {event.is_active ? '진행중' : '종료'}
                </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{event.title}</h1>
            
            <div className="flex items-center text-md text-gray-600 mb-6">
              <Calendar className="w-5 h-5 mr-2" />
              <span>
                {new Date(event.start_date).toLocaleDateString('ko-KR')} - {new Date(event.end_date).toLocaleDateString('ko-KR')}
              </span>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="whitespace-pre-wrap">{event.description}</p>
            </div>
          </div>
        </div>
    </div>
  );
}
